import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import Http from "../services/Http";
import { toastHandler } from "../../utils/utils";
import { messages } from "../../utils/messages";

// Initial state for the course slice
const initialState = {
  // zoneData: {},
  courseData: {
    data: [], // Initialize data as an empty array
  },
  fixtureData: [],
  courseFixtureData: [],
  courseIncidentTypesData: [],
  courseFixtureIncidentData: [],
  status: "idle",
  locationId: "",
};

// Destructure the httpRequest function from Http service
const { httpRequest } = Http();

/**
 * Async thunk for fetching the list of course.
 * @returns {Array} - List of course.
 */
export const courseList = createAsyncThunk(
  "listCourse",
  async ({ locationId, pageCount, navigate, publish }, { rejectWithValue }) => {
    try {
      const data = await httpRequest(
        {
          method: "GET",
          url: `admin/courses?location_id=${locationId}&page=${pageCount}`,
        },
        navigate,
      );
      return {
        data,
        locationId,
        publish,
      };
    } catch (err) {
      return rejectWithValue(err.message || messages.listCourse.error, {
        cause: err.cause,
      });
    }
  },
);

/**
 * Async thunk for printing a course.
 * This function dispatches a POST request to print the specified course.
 *
 * @param {Object} param - The parameter object.
 * @param {string} param.id - The ID of the course to be printed.
 * @param {Object} thunkAPI - The thunk API object, providing dispatch, getState, and other utilities.
 * @returns {Object} - The response from the HTTP request, or an error object in case of failure.
 *
 * @throws Will throw an error if the HTTP request fails.
 */
// export const printCourse = createAsyncThunk(
//   "printingCourse",
//   async ({ id, testKey, navigate }, { rejectWithValue }) => {
//     try {
//       return await httpRequest(
//         {
//           method: "POST",
//           body: {
//             course_id: id,
//             test: testKey,
//           },
//           url: "admin/course/print",
//         },
//         navigate
//       );
//     } catch (err) {
//       return rejectWithValue(err.message || messages.printingCourse.error, {
//         cause: err.cause,
//       });
//     }
//   }
// );

/**
 * Async thunk for publish a course.
 * This function dispatches a POST request to print the specified course.
 *
 * @param {Object} param - The parameter object.
 * @param {string} param.id - The ID of the course to be publish.
 * @param {Object} thunkAPI - The thunk API object, providing dispatch, getState, and other utilities.
 * @returns {Object} - The response from the HTTP request, or an error object in case of failure.
 *
 * @throws Will throw an error if the HTTP request fails.
 */
export const publishCourse = createAsyncThunk(
  "publishedCourse",
  async ({ id, navigate }, { rejectWithValue }) => {
    try {
      const blob = await httpRequest(
        {
          method: "POST",
          body: { zone_id: id },
          url: "admin/zones/publish",
          responseType: "blob", // Set response type to blob
        },
        navigate,
      );

      // Do something with the Blob, such as generating a URL
      const url = URL.createObjectURL(blob);

      return { id, url }; // Return the URL instead of the Blob
    } catch (err) {
      return rejectWithValue(err.message || "Error publishing zone", {
        cause: err.cause,
      });
    }
  },
);

/**
 * Async thunk for downloading SCORM for a quest (per incident type).
 * This replaces the old /courses/scorm endpoint.
 *
 * @param {Object} param - The parameter object.
 * @param {number} param.courseId - The ID of the course.
 * @param {number} param.incidentTypeId - The ID of the incident type (quest).
 * @param {boolean} param.test - Whether to download test SCORM (defaults to true).
 * @param {Object} thunkAPI - The thunk API object.
 * @returns {Object} - The response containing the download URL.
 *
 * @throws Will throw an error if the HTTP request fails.
 */
export const downloadCourse = createAsyncThunk(
  "downloadedCourse",
  async ({ courseId, incidentTypeId, test = true, navigate }, { rejectWithValue }) => {
    try {
      const blob = await httpRequest(
        {
          method: "POST",
          body: {
            test,
          },
          url: `admin/courses/${courseId}/quest/${incidentTypeId}/scorm`,
          responseType: "blob", // Set response type to blob
        },
        navigate,
      );

      // Do something with the Blob, such as generating a URL
      const url = URL.createObjectURL(blob);

      return { courseId, incidentTypeId, url }; // Return the URL instead of the Blob
    } catch (err) {
      return rejectWithValue(err.message || "Error downloading SCORM", {
        cause: err.cause,
      });
    }
  },
);

/**
 * Async thunk for adding a new course.
 * @param {Object} data - The course data to be added.
 * @returns {Object} - The added course data.
 */
export const addCourse = createAsyncThunk(
  "createCourse",
  async ({ data, navigate }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "POST",
          body: data,
          url: "admin/courses",
        },
        navigate,
      );
    } catch (err) {
      return rejectWithValue(err.message || messages.createCourse.error, {
        cause: err.cause,
      });
    }
  },
);

/**
 * Async thunk for updating an existing course.
 * @param {Object} data - The course data to be updated.
 * @param {string} id - The ID of the course to be updated.
 * @returns {Object} - The updated course data.
 */
export const updateCourse = createAsyncThunk(
  "editCourse",
  async ({ data, id, courseId, navigate }, { rejectWithValue }) => {
    try {
      await httpRequest(
        {
          method: "PUT",
          body: data,
          url: `admin/courses/${id}`,
        },
        navigate,
      );
      return { data, id, courseId };
    } catch (err) {
      return rejectWithValue(err.message || messages.editCourse.error, {
        cause: err.cause,
      });
    }
  },
);

/**
 * Async thunk for deleting a course by ID.
 * @param {string} id - The ID of the course to be deleted.
 * @returns {Object} - The response from the delete request.
 */
export const deleteCourse = createAsyncThunk(
  "deletedCourse",
  async ({ id, locationId, navigate }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "DELETE",
          url: `admin/courses/${id}?location_id=${locationId}`,
        },
        navigate,
      );
    } catch (err) {
      return rejectWithValue(err.message || messages.deletedCourse.error, {
        cause: err.cause,
      });
    }
  },
);

/**
 * Async thunk for updating GPS status of a quest (per incident type).
 * @param {Object} data - The data needed for the request.
 * @param {number} data.courseId - The ID of the course.
 * @param {number} data.incidentTypeId - The ID of the incident type (quest).
 * @param {boolean} data.gps - The GPS status (true or false).
 * @returns {Object} - The API response (204 No Content).
 */
export const updateGpsStatus = createAsyncThunk(
  "updateGpsStatus",
  async ({ courseId, incidentTypeId, gps }, { rejectWithValue }) => {
    try {
      const response = await httpRequest({
        method: "POST",
        url: `admin/courses/fixtures/toggle/gps`,
        body: {
          course_id: courseId,
          incident_type_id: incidentTypeId,
          gps,
        },
      });
      return response;
    } catch (err) {
      return rejectWithValue(err.message || "Failed to update GPS status", {
        cause: err.cause,
      });
    }
  },
);

/**
 * Async thunk for updating Prompt-Search status of a quest (per incident type).
 * @param {Object} data - The data needed for the request.
 * @param {number} data.courseId - The ID of the course.
 * @param {number} data.incidentTypeId - The ID of the incident type (quest).
 * @param {boolean} data.promptSearch - The prompt-search status (true or false).
 * @returns {Object} - The API response (204 No Content).
 */
export const updatePromptSearchStatus = createAsyncThunk(
  "updatePromptSearchStatus",
  async ({ courseId, incidentTypeId, promptSearch }, { rejectWithValue }) => {
    try {
      const response = await httpRequest({
        method: "POST",
        url: `admin/courses/fixtures/toggle/prompt-search`,
        body: {
          course_id: courseId,
          incident_type_id: incidentTypeId,
          prompt_search: promptSearch,
        },
      });
      return response;
    } catch (err) {
      return rejectWithValue(err.message || "Failed to update Prompt-Search status", {
        cause: err.cause,
      });
    }
  },
);

/**
 * Async thunk for updating Auto-Prompt status of a quest (per incident type).
 * @param {Object} data - The data needed for the request.
 * @param {number} data.courseId - The ID of the course.
 * @param {number} data.incidentTypeId - The ID of the incident type (quest).
 * @param {boolean} data.autoPrompt - The auto-prompt status (true or false).
 * @returns {Object} - The API response (204 No Content).
 */
export const updateAutoPromptStatus = createAsyncThunk(
  "updateAutoPromptStatus",
  async ({ courseId, incidentTypeId, autoPrompt }, { rejectWithValue }) => {
    try {
      const response = await httpRequest({
        method: "POST",
        url: `admin/courses/fixtures/toggle/auto-prompt`,
        body: {
          course_id: courseId,
          incident_type_id: incidentTypeId,
          auto_prompt: autoPrompt,
        },
      });
      return response;
    } catch (err) {
      return rejectWithValue(err.message || "Failed to update Auto-Prompt status", {
        cause: err.cause,
      });
    }
  },
);

/**
 * Async thunk for updating GPS status of a course.
 * @param {Object} data - The data needed for the request.
 * @param {string} data.courseId - The ID of the course to be updated.
 * @param {boolean} data.gps - The GPS status (true or false).
 * @param {string} data.locationId - The location ID.
 * @returns {Object} - The API response.
 */
export const updateDigitalStatus = createAsyncThunk(
  "updateDigitalStatus",
  async (
    { digital_only, courseId, incidentTypeId, fixtureId },
    { rejectWithValue },
  ) => {
    try {
      const response = await httpRequest({
        method: "POST",
        url: `admin/courses/fixtures/toggle/digital-only`,
        body: {
          course_id: courseId,
          incident_type_id: incidentTypeId,
          fixture_id: fixtureId,
          digital_only,
        },
      });
      return response;
    } catch (err) {
      return rejectWithValue(
        err.message || "Failed to update Digital Only status",
        {
          cause: err.cause,
        },
      );
    }
  },
);

/**
 * Async thunk for adding a clone course.
 * @param {Object} data - The course data to be added.
 * @returns {Object} - The added course data.
 */
export const copyCourse = createAsyncThunk(
  "cloneCourse",
  async (
    { courseId, courseName, courseNotes, courseLocationId, navigate },
    { rejectWithValue },
  ) => {
    try {
      return await httpRequest(
        {
          method: "POST",
          body: {
            name: courseName,
            notes: courseNotes,
            location_id: courseLocationId,
          },
          url: `admin/courses/${courseId}/clone`,
        },
        navigate,
      );
    } catch (err) {
      return rejectWithValue(err.message || messages.cloneCourse.error, {
        cause: err.cause,
      });
    }
  },
);

/**
 * Async thunk for copying a quest (per incident type).
 * Creates a brand new CourseGroup + Course containing only the copied quest
 * with its selected prompts and toggle settings.
 *
 * @param {Object} data - The data needed for the request.
 * @param {number} data.courseId - The ID of the source course.
 * @param {number} data.incidentTypeId - The ID of the incident type (quest) to copy.
 * @param {string} data.name - The name for the new quest (required, max 100 chars).
 * @param {string} data.color - Optional hex color string.
 * @param {number} data.locationId - The location ID (required).
 * @returns {Object} - Response containing { course: { id, name, course_group_id, courseGroup } }.
 */
export const copyQuest = createAsyncThunk(
  "copyQuest",
  async (
    { courseId, incidentTypeId, name, color, locationId, navigate },
    { rejectWithValue },
  ) => {
    try {
      const body = {
        name,
        location_id: locationId,
      };
      if (color) {
        body.color = color;
      }
      return await httpRequest(
        {
          method: "POST",
          body,
          url: `admin/courses/${courseId}/quest/${incidentTypeId}/copy`,
        },
        navigate,
      );
    } catch (err) {
      return rejectWithValue(err.message || "Failed to copy quest", {
        cause: err.cause,
      });
    }
  },
);

/**
 * Async thunk for cloning an entire CourseGroup (Quest Area).
 * Creates a new CourseGroup with all its courses, incident types, and incident selections.
 *
 * @param {Object} data - The data needed for the request.
 * @param {number} data.courseGroupId - The ID of the CourseGroup to clone.
 * @param {number} data.locationId - The location ID (required).
 * @param {string} data.startingMarker - The name for the new starting marker (required).
 * @param {string} data.color - Hex color string (required).
 * @returns {Object} - Response containing the new course_group with its courses.
 */
export const cloneCourseGroup = createAsyncThunk(
  "cloneCourseGroup",
  async (
    { courseGroupId, locationId, startingMarker, color, navigate },
    { rejectWithValue },
  ) => {
    try {
      const body = {
        location_id: locationId,
        starting_marker: startingMarker,
        color: color,
      };
      return await httpRequest(
        {
          method: "POST",
          body,
          url: `admin/courses/groups/${courseGroupId}/clone`,
        },
        navigate,
      );
    } catch (err) {
      return rejectWithValue(err.message || "Failed to clone quest area", {
        cause: err.cause,
      });
    }
  },
);

/**
 * Async thunk for deleting a quest (per incident type) from a course.
 * This only removes the quest type from the course, not the course itself.
 *
 * @param {Object} data - The data needed for the request.
 * @param {number} data.courseId - The ID of the course.
 * @param {number} data.incidentTypeId - The ID of the incident type (quest) to delete.
 * @returns {Object} - The API response (204 No Content).
 */
export const deleteQuest = createAsyncThunk(
  "deleteQuest",
  async ({ courseId, incidentTypeId }, { rejectWithValue }) => {
    try {
      const response = await httpRequest({
        method: "POST",
        url: `admin/courses/fixtures/delete-quest`,
        body: {
          course_id: courseId,
          incident_type_id: incidentTypeId,
        },
      });
      return response;
    } catch (err) {
      return rejectWithValue(err.message || "Failed to delete quest", {
        cause: err.cause,
      });
    }
  },
);

/**
 * Async thunk for launching/testing a quest (per incident type).
 * This replaces the old /courses/{id}/test/link endpoint.
 * Note: Quest must be published first or it returns 400.
 *
 * @param {Object} data - The data needed for the request.
 * @param {number} data.courseId - The ID of the course.
 * @param {number} data.incidentTypeId - The ID of the incident type (quest).
 * @returns {Object} - Response containing { uuid, qrcodeurl }.
 */
export const testCourse = createAsyncThunk(
  "testingCourseLink",
  async ({ courseId, incidentTypeId, navigate }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "POST",
          url: `admin/courses/${courseId}/quest/${incidentTypeId}/test`,
        },
        navigate,
      );
    } catch (err) {
      return rejectWithValue(err.message || messages.testingCourseLink.error, {
        cause: err.cause,
      });
    }
  },
);

/**
 * Async thunk for publishing a quest (per incident type).
 * This replaces the old /courses/{id}/print endpoint.
 *
 * @param {Object} param - The parameter object.
 * @param {number} param.courseId - The ID of the course.
 * @param {number} param.incidentTypeId - The ID of the incident type (quest).
 * @param {string} param.testKey - Whether this is a test ("true"/"false").
 * @param {boolean} param.canvas - Whether to include canvas data.
 * @param {boolean} param.html - Whether to include HTML data.
 * @param {Object} thunkAPI - The thunk API object.
 * @returns {Object} - The response from the HTTP request.
 *
 * @throws Will throw an error if the HTTP request fails.
 */
export const printCourse = createAsyncThunk(
  "printingCourse",
  async ({ courseId, incidentTypeId, testKey, canvas = false, html = true, navigate }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "POST",
          body: {
            take_test: testKey ? "true" : "false",
            canvas,
            html,
          },
          url: `admin/courses/${courseId}/quest/${incidentTypeId}/publish`,
        },
        navigate,
      );
    } catch (err) {
      return rejectWithValue(err.message || messages.printingCourse.error, {
        cause: err.cause,
      });
    }
  },
);

/**
 * Async thunk for getting Fixture by course ID.
 * @param {string} id - The ID of the course.
 * @returns {Array} - List of Fixture.
 */
export const getFixture = createAsyncThunk(
  "zoneFixture",
  async ({ id, navigate }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "GET",
          url: `admin/courses/fixtures/${id}`,
        },
        navigate,
      );
    } catch (err) {
      return rejectWithValue(err.message || messages.getFixture.error, {
        cause: err.cause,
      });
    }
  },
);

export const getIncidentTypes = createAsyncThunk(
  "courseIncident",
  async ({ courseId, navigate }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "GET",
          url: `admin/incidents/types?course_id=${courseId}`,
        },
        navigate,
      );
    } catch (err) {
      return rejectWithValue(err.message || messages.getIncidentTypes.error, {
        cause: err.cause,
      });
    }
  },
);

/**
 * Async thunk for getting Fixture by course ID.
 * @param {string} id - The ID of the course.
 * @returns {Array} - List of Fixture.
 */
export const getCourseFixtureIncident = createAsyncThunk(
  "courseFixtureIncident",
  async ({ id, navigate }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "GET",
          url: `admin/courses/fixtures/${id}/incidents`,
          // url: `admin/zone-fixtures/incidents/${id}`,
        },
        navigate,
      );
    } catch (err) {
      return rejectWithValue(
        err.message || messages.getCourseFixtureIncident.error,
        {
          cause: err.cause,
        },
      );
    }
  },
);

/**
 * Async thunk for deleting a course fixture by ID.
 * @param {string} id - The ID of the course fixture to be deleted.
 * @returns {Object} - The response from the delete request.
 */
export const deleteCourseFixture = createAsyncThunk(
  "deletedZoneFixture",
  async ({ id, selectedCourseId, navigate }, { rejectWithValue }) => {
    try {
      await httpRequest(
        {
          method: "DELETE",
          url: `admin/zone-fixtures/${id}?zone_id=${selectedCourseId}`,
        },
        navigate,
      );
      return { id };
    } catch (err) {
      return rejectWithValue(err.message || messages.deletedZoneFixture.error, {
        cause: err.cause,
      });
    }
  },
);

/**
 * Async thunk for fetching the list of campuses.
 * @returns {Array} - List of campuses.
 */
export const getAllFixture = createAsyncThunk(
  "fixtures",
  async (_, { rejectWithValue }) => {
    try {
      return await httpRequest({
        method: "GET",
        url: "admin/fixtures",
      });
    } catch (err) {
      return rejectWithValue(err.message || messages.listFixtures.error, {
        cause: err.cause,
      });
    }
  },
);

// Create a slice for course-related actions and state management
const courseSlice = createSlice({
  name: "coursePoint",
  initialState,
  reducers: {
    /**
     * Reducer for showing toast messages.
     * @param {Object} state - The current state.
     * @param {Object} action - The action containing payload data.
     */
    showToast: (state, action) => {
      const { success, message, navigate, navigatePath, locationState } =
        action.payload;
      toastHandler(success, message, navigate, navigatePath, locationState);
    },
    setClearCourseData: (state, action) => {
      state.courseData = [];
    },
    // updateZoneFixturesCount: (state, action) => {
    //   state.zoneData.data = state.zoneData.data.map((item) => {
    //     if (item.id === parseInt(action.payload)) {
    //       return {
    //         ...item,
    //         course_fixtures_count: item.course_fixtures_count + 1,
    //       };
    //     }
    //     return item;
    //   });
    // },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        (action) =>
          [
            courseList.pending,
            printCourse.pending,
            publishCourse.pending,
            downloadCourse.pending,
            addCourse.pending,
            deleteCourse.pending,
            updateCourse.pending,
            updateGpsStatus.pending,
            updateDigitalStatus.pending,
            updatePromptSearchStatus.pending,
            updateAutoPromptStatus.pending,
            copyCourse.pending,
            copyQuest.pending,
            cloneCourseGroup.pending,
            deleteQuest.pending,
            testCourse.pending,
            getFixture.pending,
            getCourseFixtureIncident.pending,
            getAllFixture.pending,
            deleteCourseFixture.pending,
          ].some((type) => type.match(action)),
        (state, action) => {
          state.status = "loading";
          action.meta.arg.setLoader(true);
        },
      )
      .addMatcher(
        (action) =>
          [
            courseList.fulfilled,
            printCourse.fulfilled,
            publishCourse.fulfilled,
            downloadCourse.fulfilled,
            addCourse.fulfilled,
            deleteCourse.fulfilled,
            updateCourse.fulfilled,
            updateGpsStatus.fulfilled,
            updateDigitalStatus.fulfilled,
            updatePromptSearchStatus.fulfilled,
            updateAutoPromptStatus.fulfilled,
            copyCourse.fulfilled,
            copyQuest.fulfilled,
            cloneCourseGroup.fulfilled,
            deleteQuest.fulfilled,
            testCourse.fulfilled,
            getFixture.fulfilled,
            getCourseFixtureIncident.fulfilled,
            getAllFixture.fulfilled,
            deleteCourseFixture.fulfilled,
            getIncidentTypes.fulfilled,
          ].some((type) => type.match(action)),
        (state, action) => {
          //  console.log("stateeee", state);
          // console.log("action", action);

          state.status = "succeeded";

          // if (["listCourse"].includes(action.type.split("/")[0])) {
          //   state.zoneData = action.payload.zones;
          // }

          // Update state in case of edit
          // if (action.type === "editCourse/fulfilled") {
          //   const selectedzone = state.zoneData.data.find(
          //     (zone) => zone.id === action.payload.id
          //   );
          //   if (selectedzone) {
          //     selectedzone.name = action.payload.data.name;
          //     selectedzone.notes = action.payload.data.notes;
          //   }
          // }

          if (action.type === "listCourse/fulfilled") {
            // August
            if (action.payload.publish === "publish") {
              state.courseData = {
                data: [],
              };
            }

            if (state.locationId !== action.payload.locationId) {
              state.courseData = {
                data: [],
              };
            }

            state.course_fixtures_count =
              action.payload.data.course_fixtures_count;
            state.locationId = action.payload.locationId;

            // const existingData = state.zoneData.data;
            // const newData = action.payload.data.zones.data;
            const existingData = Array.isArray(state.courseData.data)
              ? state.courseData.data
              : [];

            const newData = Array.isArray(action.payload.data?.courses?.data)
              ? action.payload.data.courses.data
              : [];

            const mergedData = [...newData, ...existingData].reduce(
              (acc, current) => {
                if (!acc.find((item) => item.id === current.id)) {
                  acc.push(current);
                }
                return acc;
              },
              [],
            );

            state.courseData = {
              ...action.payload.data.courses,
              data: mergedData,
            };
          }

          // Create
          if ("createCourse" === action.type.split("/")[0]) {
            state.courseData = {
              data: [],
            };
          }

          // Update state in case of edit
          if (action.type.startsWith("editCourse")) {
            const { courseId } = action.payload; // Destructure courseId from action payload
            console.log("Updating course with ID:", courseId); // Log the courseId

            // Find the course data by courseId
            const courseData = state.courseData.data.find(
              (course) => course.id === courseId,
            );

            if (courseData) {
              const selectedCourse = courseData.courses.find(
                (course) => course.id === action.payload.id,
              );

              if (selectedCourse) {
                // Update selected course with new data
                Object.assign(selectedCourse, {
                  starting_marker: action.payload.data.starting_marker,
                  name: action.payload.data.name,
                  notes: action.payload.data.notes,
                });
                console.log("Updated selectedCourse:", selectedCourse); // Log the updated course
              } else {
                console.warn("No course found with ID:", action.payload.id); // Log a warning if no course found
              }
            } else {
              console.warn("No course found with ID:", courseId); // Log a warning if no course data found
            }
          }

          // Delete
          if ("deletedCourse" === action.type.split("/")[0]) {
            state.courseData = {
              data: [],
            };
          }

          // Clone
          if ("cloneCourse" === action.type.split("/")[0]) {
            state.courseData = {
              data: [],
            };
          }

          // updateGpsStatus
          if ("updateGpsStatus" === action.type.split("/")[0]) {
            state.courseData = {
              data: [],
            };
          }

          // updateDigitalStatus
          if ("updateDigitalStatus" === action.type.split("/")[0]) {
            state.courseData = {
              data: [],
            };
          }

          // updatePromptSearchStatus
          if ("updatePromptSearchStatus" === action.type.split("/")[0]) {
            state.courseData = {
              data: [],
            };
          }

          // updateAutoPromptStatus
          if ("updateAutoPromptStatus" === action.type.split("/")[0]) {
            state.courseData = {
              data: [],
            };
          }

          // copyQuest
          if ("copyQuest" === action.type.split("/")[0]) {
            state.courseData = {
              data: [],
            };
          }

          // deleteQuest
          if ("deleteQuest" === action.type.split("/")[0]) {
            state.courseData = {
              data: [],
            };
          }

          // testingCourseLink
          // if ("testingCourseLink" === action.type.split("/")[0]) {
          //   state.courseData = {
          //     data: [],
          //   };
          // }

          // August
          // if ("printingCourse" === action.type.split("/")[0]) {
          //   state.zoneData = {
          //     data: [],
          //   };
          // }

          // get fixtures data
          if (action.type === "fixtures/fulfilled") {
            state.fixtureData = action.payload.fixtures;
          }

          //get course fixtures data
          if (action.type === "zoneFixture/fulfilled") {
            state.courseFixtureData = action.payload.courseFixtures;
          }

          if (action.type === "courseIncident/fulfilled") {
            state.courseIncidentTypesData = action.payload?.incidentTypes || [];
          }

          console.log("IncidentTypes:", state.courseIncidentTypesData);

          //get course fixtures incident data
          if (action.type === "courseFixtureIncident/fulfilled") {
            state.courseFixtureIncidentData = action.payload.incidentTypes;
          }

          // Update course fixtures delete
          if (action.type === "deletedZoneFixture/fulfilled") {
            state.courseFixtureData = state.courseFixtureData.filter(
              (fixture) => fixture.id !== action.payload.id,
            );
          }

          action.meta.arg.setLoader(false);
          // Show toast only in case of add, edit, delete Assembly
          if (
            [
              // "printingCourse/fulfilled",
              // "publishedCourse/fulfilled",
              "createCourse/fulfilled",
              "editCourse/fulfilled",
              "deletedCourse/fulfilled",
              "updateGpsStatus/fulfilled",
              "updateDigitalStatus/fulfilled",
              "updatePromptSearchStatus/fulfilled",
              "updateAutoPromptStatus/fulfilled",
              "cloneCourse/fulfilled",
              "copyQuest/fulfilled",
              "deleteQuest/fulfilled",
              "testingCourseLink/fulfilled",
              "deletedZoneFixture/fulfilled",
            ].includes(action.type)
          ) {
            courseSlice.caseReducers.showToast(state, {
              payload: {
                success: true,
                message:
                  action?.payload?.message ||
                  messages[action.type.split("/")[0]]?.success,
              },
            });
          }
        },
      )
      .addMatcher(
        (action) =>
          [
            courseList.rejected,
            printCourse.rejected,
            publishCourse.rejected,
            downloadCourse.rejected,
            addCourse.rejected,
            deleteCourse.rejected,
            updateCourse.rejected,
            updateGpsStatus.rejected,
            updateDigitalStatus.rejected,
            updatePromptSearchStatus.rejected,
            updateAutoPromptStatus.rejected,
            copyCourse.rejected,
            copyQuest.rejected,
            cloneCourseGroup.rejected,
            deleteQuest.rejected,
            testCourse.rejected,
            getFixture.rejected,
            getCourseFixtureIncident.rejected,
            getAllFixture.rejected,
            deleteCourseFixture.rejected,
          ].some((type) => type.match(action)),
        (state, action) => {
          state.status = "failed";
          action.meta.arg.setLoader(false);
          courseSlice.caseReducers.showToast(state, {
            payload: {
              success: false,
              message: action.payload,
            },
          });
        },
      );
  },
});

// export const { updateZoneFixturesCount } = courseSlice.actions;

// Action creators are generated for each case reducer function
export const { setClearCourseData } = courseSlice.actions;

export default courseSlice.reducer;
